"use client"

import { useContext } from "react"
import { Box, IconButton, Typography, Tooltip, Avatar, useTheme, Badge } from "@mui/material"
import {
  LightModeOutlined as LightModeIcon,
  DarkModeOutlined as DarkModeIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material"
import { useSelector, useDispatch } from "react-redux"
import { logoutUser } from "../../redux/actions/user"
import { useNavigate } from "react-router-dom"
import { ColorModeContext, tokens } from "../../theme"

const Topbar = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const colorMode = useContext(ColorModeContext)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.user)

  const handleLogout = () => {
    dispatch(logoutUser())
    navigate("/login")
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        backgroundColor: theme.palette.mode === "light" ? "#ffffff" : colors.grey[500],
        boxShadow: `0 4px 12px ${colors.primary[500]}15`,
        position: "sticky",
        top: -20,
        zIndex: 1100,
        borderBottom: `2px solid ${colors.primary[500]}`,
        transition: "all 0.3s ease",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          letterSpacing: 1,
          color: colors.primary[500],
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "scale(1.02)",
          },
        }}
      >
        SOBEP
      </Typography>

      <Box display="flex" alignItems="center" gap={1.5}>
        {/* Theme Toggle Button */}
        <Tooltip title="Switch Theme" arrow>
          <IconButton
            onClick={colorMode.toggleColorMode}
            sx={{
              backgroundColor: colors.primary[500],
              color: "white",
              borderRadius: "50%",
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: colors.primary[600],
                transform: "rotate(180deg)",
                boxShadow: `0 4px 12px ${colors.primary[500]}40`,
              },
            }}
          >
            {theme.palette.mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            height: 24,
            width: 1,
            backgroundColor: colors.grey[400],
            opacity: 0.3,
          }}
        />

        {/* User Info and Logout */}
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.mode === "light" ? colors.grey[900] : colors.grey[100],
              }}
            >
              {user?.name || "User"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.primary[500],
                fontSize: "0.7rem",
              }}
            >
              {user?.role || "Admin"}
            </Typography>
          </Box>

          {/* User Avatar */}
          <Badge
            overlap="circular"
            variant="dot"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: colors.primary[500],
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                height: 12,
                minWidth: 12,
                borderRadius: "50%",
              },
            }}
          >
            <Avatar
              src="/placeholder.svg"
              alt="User Avatar"
              sx={{
                width: 40,
                height: 40,
                cursor: "pointer",
                border: `2px solid ${colors.primary[500]}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: `0 0 12px ${colors.primary[500]}50`,
                  transform: "scale(1.05)",
                },
              }}
            />
          </Badge>

          {/* Logout Button */}
          <Tooltip title="Logout" arrow>
            <IconButton
              onClick={handleLogout}
              sx={{
                backgroundColor: colors.grey[300],
                color: colors.grey[900],
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: colors.grey[400],
                  transform: "scale(1.1)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  )
}

export default Topbar
